import { CurrencyPipe, DatePipe } from "@angular/common";
import { Component, Input, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { IApiResponse } from "@models/api-response.model";
import { RequestService } from "@services/index";

interface StagedListing {
  id: string;
  caption: string | null;
  postedAt: string;
  /** How many listings came out of the same Telegram post (they share its photo). */
  siblings: number;
  productName: string | null;
  description: string | null;
  price: number | null;
  optionName: string | null;
  optionPrice: number | null;
  sourcePrice: number | null;
  sourceOptionPrice: number | null;
  markup: number;
  qty: number | null;
  minSize: number | null;
  maxSize: number | null;
  categoryId: number | null;
  subcategoryId: number | null;
  subsubcategoryId: number | null;
  categoryPath: string | null;
  categoryEdited: boolean;
  imageCount: number;
  imageDataUrl: string | null;
}

interface IngestResult {
  posts: number;
  staged: number;
  /** Skipped because this vendor already sells a product of that exact name. */
  alreadyListed: number;
  multiListingPosts: number;
  skippedNoMedia: number;
  skippedNotListing: number;
  failures: string[];
}

interface AuthStatus {
  configured: boolean;
  connected: boolean;
  phone: string;
}

interface CategoryOption {
  id: number;
  category_name: string;
}

/** Per-card UI state that isn't part of the listing itself. */
interface CardState {
  subs: CategoryOption[];
  leaves: CategoryOption[];
  saving: boolean;
  confirming: boolean;
  confirmedProductId: number | null;
  error: string;
}

/**
 * Abata Aquatics' Product Sync panel: pull the seller's recent Telegram posts, read
 * each one into a proposed listing, and put the ones that look right into the
 * catalogue — one Confirm at a time.
 *
 * Nothing here is automatic. The pull only stages; the catalogue is written by Confirm
 * alone, and a staged pull is transient (24h, and gone on a backend restart), so the
 * page always offers Ingest rather than assuming a tray is waiting.
 */
@Component({
  selector: "fh-telegram-ingest-panel",
  templateUrl: "./telegram-ingest-panel.component.html",
  styleUrls: ["./telegram-ingest-panel.component.scss"],
  standalone: true,
  imports: [
    CurrencyPipe,
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
  ],
})
export class TelegramIngestPanelComponent implements OnInit {
  @Input({ required: true }) vendorId!: string;

  staged = signal<StagedListing[]>([]);
  expiresAt = signal<string | null>(null);
  result = signal<IngestResult | null>(null);
  ingesting = signal<boolean>(false);
  loading = signal<boolean>(true);
  error = signal<string>("");

  // Sign-in state. The session string is shown once, for pasting into the environment —
  // this backend deliberately never stores it.
  auth = signal<AuthStatus | null>(null);
  loginStep = signal<"idle" | "code" | "password" | "done">("idle");
  loginBusy = signal<boolean>(false);
  loginError = signal<string>("");
  loginCode = "";
  loginPassword = "";
  newSession = signal<string>("");
  sessionCopied = signal<boolean>(false);

  /** Main categories, loaded once; sub/subsub are fetched per card as they're chosen. */
  mainCategories = signal<CategoryOption[]>([]);
  cards = new Map<string, CardState>();

  constructor(private requestService: RequestService) {}

  ngOnInit() {
    this.loadAuth();
    this.loadCategories();
    this.refresh();
  }

  private loadAuth() {
    this.requestService.get<IApiResponse>("/api/v2/telegram-ingest/auth").subscribe({
      next: (res) => this.auth.set((res.data as AuthStatus) ?? null),
      error: () => this.auth.set(null),
    });
  }

  /** Ask Telegram to send the login code. */
  startLogin() {
    this.loginBusy.set(true);
    this.loginError.set("");
    this.requestService.post<IApiResponse>("/api/v2/telegram-ingest/auth/start", {}).subscribe({
      next: (res) => {
        this.loginBusy.set(false);
        if (!res.status) {
          this.loginError.set(res.message || "Could not start sign-in");
          return;
        }
        this.loginStep.set("code");
      },
      error: (err) => {
        this.loginBusy.set(false);
        this.loginError.set(err?.error?.message || "Could not start sign-in");
      },
    });
  }

  /** Send the code, and the 2FA password on the second pass if Telegram asks for one. */
  completeLogin() {
    this.loginBusy.set(true);
    this.loginError.set("");
    this.requestService
      .post<IApiResponse>("/api/v2/telegram-ingest/auth/complete", {
        code: this.loginCode,
        password: this.loginPassword || undefined,
      })
      .subscribe({
        next: (res) => {
          this.loginBusy.set(false);
          if (!res.status) {
            this.loginError.set(res.message || "Sign-in failed");
            return;
          }
          const data = res.data as { session?: string; needsPassword?: boolean };
          if (data?.needsPassword) {
            this.loginStep.set("password");
            return;
          }
          this.newSession.set(data?.session ?? "");
          this.loginStep.set("done");
          this.loginCode = "";
          this.loginPassword = "";
        },
        error: (err) => {
          this.loginBusy.set(false);
          this.loginError.set(err?.error?.message || "Sign-in failed");
        },
      });
  }

  copySession() {
    navigator.clipboard?.writeText(this.newSession()).then(
      () => this.sessionCopied.set(true),
      () => this.sessionCopied.set(false)
    );
  }

  cancelLogin() {
    this.loginStep.set("idle");
    this.loginError.set("");
    this.loginCode = "";
    this.loginPassword = "";
  }

  state(id: string): CardState {
    let s = this.cards.get(id);
    if (!s) {
      s = {
        subs: [],
        leaves: [],
        saving: false,
        confirming: false,
        confirmedProductId: null,
        error: "",
      };
      this.cards.set(id, s);
    }
    return s;
  }

  private loadCategories() {
    this.requestService
      .get<IApiResponse>("/api/v2/categories?parent_id=0&status=active&pageSize=500")
      .subscribe({
        next: (res) => this.mainCategories.set(this.categoriesFrom(res)),
        error: () => this.mainCategories.set([]),
      });
  }

  /** The categories endpoint returns either a bare array or {categories}; accept both. */
  private categoriesFrom(res: IApiResponse): CategoryOption[] {
    const data = res?.data as unknown;
    if (Array.isArray(data)) return data as CategoryOption[];
    const inner = (data as { categories?: CategoryOption[] })?.categories;
    return Array.isArray(inner) ? inner : [];
  }

  private children(parentId: number) {
    // pageSize is explicit: the endpoint defaults to 25, and Marine Fish alone has 37
    // types — without it the Type dropdown would quietly drop a dozen options.
    return this.requestService.get<IApiResponse>(
      `/api/v2/categories?parent_id=${parentId}&status=active&pageSize=500`
    );
  }

  /** Existing tray, if the backend still holds one. */
  refresh() {
    this.loading.set(true);
    this.requestService
      .get<IApiResponse>(`/api/v2/vendor/${this.vendorId}/telegram-ingest/staged`)
      .subscribe({
        next: (res) => {
          this.loading.set(false);
          if (!res.status) {
            this.error.set(res.message || "Could not load staged listings");
            return;
          }
          const data = res.data as { staged: StagedListing[]; expiresAt: string | null };
          this.staged.set(data.staged ?? []);
          this.expiresAt.set(data.expiresAt ?? null);
          this.primeDropdowns();
        },
        error: () => {
          this.loading.set(false);
          this.error.set("Could not load staged listings");
        },
      });
  }

  ingest() {
    this.ingesting.set(true);
    this.error.set("");
    this.requestService
      .post<IApiResponse>(`/api/v2/vendor/${this.vendorId}/telegram-ingest`, {})
      .subscribe({
        next: (res) => {
          this.ingesting.set(false);
          if (!res.status) {
            this.error.set(res.message || "Ingest failed");
            return;
          }
          const data = res.data as {
            staged: StagedListing[];
            expiresAt: string | null;
            result: IngestResult;
          };
          this.cards.clear();
          this.staged.set(data.staged ?? []);
          this.expiresAt.set(data.expiresAt ?? null);
          this.result.set(data.result ?? null);
          this.primeDropdowns();
        },
        error: (err) => {
          this.ingesting.set(false);
          this.error.set(err?.error?.message || "Ingest failed");
        },
      });
  }

  /** Load the sub/subsub lists for whatever each card is already filed under. */
  private primeDropdowns() {
    for (const listing of this.staged()) {
      const state = this.state(listing.id);
      if (listing.categoryId) {
        this.children(listing.categoryId).subscribe({
          next: (res) => {
            state.subs = this.categoriesFrom(res);
          },
        });
      }
      if (listing.subcategoryId) {
        this.children(listing.subcategoryId).subscribe({
          next: (res) => {
            state.leaves = this.categoriesFrom(res);
          },
        });
      }
    }
  }

  onMainChange(listing: StagedListing, value: number | null) {
    // Picking a different tree invalidates whatever was chosen below it.
    if (value === listing.categoryId) return;
    const state = this.state(listing.id);
    listing.categoryId = value;
    listing.subcategoryId = null;
    listing.subsubcategoryId = null;
    state.subs = [];
    state.leaves = [];
    if (listing.categoryId) {
      this.children(listing.categoryId).subscribe({
        next: (res) => {
          state.subs = this.categoriesFrom(res);
        },
      });
    }
    this.saveCategory(listing);
  }

  onSubChange(listing: StagedListing, value: number | null) {
    if (value === listing.subcategoryId) return;
    const state = this.state(listing.id);
    listing.subcategoryId = value;
    listing.subsubcategoryId = null;
    state.leaves = [];
    if (listing.subcategoryId) {
      this.children(listing.subcategoryId).subscribe({
        next: (res) => {
          state.leaves = this.categoriesFrom(res);
        },
      });
    }
    this.saveCategory(listing);
  }

  onLeafChange(listing: StagedListing, value: number | null) {
    if (value === listing.subsubcategoryId) return;
    listing.subsubcategoryId = value;
    this.saveCategory(listing);
  }

  private saveCategory(listing: StagedListing) {
    if (!listing.categoryId) return;
    const state = this.state(listing.id);
    state.saving = true;
    state.error = "";
    this.requestService
      .patch<IApiResponse>(
        `/api/v2/vendor/${this.vendorId}/telegram-ingest/staged/${encodeURIComponent(listing.id)}/category`,
        {
          categoryId: listing.categoryId,
          subcategoryId: listing.subcategoryId,
          subsubcategoryId: listing.subsubcategoryId,
        }
      )
      .subscribe({
        next: (res) => {
          state.saving = false;
          if (!res.status) state.error = res.message || "Could not save category";
          else listing.categoryEdited = true;
        },
        error: (err) => {
          state.saving = false;
          state.error = err?.error?.message || "Could not save category";
        },
      });
  }

  confirm(listing: StagedListing) {
    const state = this.state(listing.id);
    state.confirming = true;
    state.error = "";
    this.requestService
      .post<IApiResponse>(
        `/api/v2/vendor/${this.vendorId}/telegram-ingest/staged/${encodeURIComponent(listing.id)}/confirm`,
        {}
      )
      .subscribe({
        next: (res) => {
          state.confirming = false;
          if (!res.status) {
            state.error = res.message || "Could not confirm";
            return;
          }
          state.confirmedProductId = (res.data as { productId: number })?.productId ?? null;
          // It has left the backend's tray; drop the card so the list matches.
          this.staged.set(this.staged().filter((s) => s.id !== listing.id));
        },
        error: (err) => {
          state.confirming = false;
          state.error = err?.error?.message || "Could not confirm";
        },
      });
  }

  sizeLabel(listing: StagedListing): string {
    if (listing.minSize == null && listing.maxSize == null) return "—";
    if (listing.minSize != null && listing.maxSize != null && listing.minSize !== listing.maxSize) {
      return `${listing.minSize}–${listing.maxSize} cm`;
    }
    return `${listing.minSize ?? listing.maxSize} cm`;
  }

  markupLabel(listing: StagedListing): string {
    return `+${Math.round((listing.markup - 1) * 100)}%`;
  }

  /** A listing is safe to confirm only once it has the fields the catalogue requires. */
  canConfirm(listing: StagedListing): boolean {
    return Boolean(listing.productName && listing.price != null && listing.categoryId);
  }
}
